import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import ScriptureCard from '../components/ScriptureCard';
import { useAuth } from '../context/AuthContext';
import { 
  BookOpen, 
  Heart, 
  Compass, 
  FlaskConical, 
  ArrowRight, 
  Library, 
  Users,
  Sparkles,
  ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hubData = [
  {
    path: '/gospel',
    title: 'Gospel',
    description: 'Foundation of grace, assurance, and the finished work of Christ',
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-1635414727475-286f0e8e02fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    step: 1,
    flow: 'Belief'
  },
  {
    path: '/identity',
    title: 'Identity Center',
    description: 'Daily reminders of who you are in Christ',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1598044520548-c1b8ef293798?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    step: 2,
    flow: 'Identity'
  },
  {
    path: '/hermeneutics',
    title: 'Hermeneutics',
    description: 'How to read and interpret the Bible responsibly',
    icon: Compass,
    image: 'https://images.unsplash.com/photo-1695730866912-fdbbf93e2250?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    step: 3,
    flow: 'Understanding'
  },
  {
    path: '/exegesis',
    title: 'Exegesis Labs',
    description: 'Step-by-step walkthroughs of biblical passages',
    icon: FlaskConical,
    image: 'https://images.unsplash.com/photo-1637109012034-e2b6b32c61c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    step: 4,
    flow: 'Practice'
  },
  {
    path: '/what-now',
    title: 'What Now?',
    description: 'Gospel living: obedience flowing from grace',
    icon: ArrowRight,
    image: 'https://images.unsplash.com/photo-1679499066019-c98cff381d81?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    step: 5,
    flow: 'Obedience'
  },
  {
    path: '/resources',
    title: 'Resources',
    description: 'Downloads, tools, and reference materials',
    icon: Library,
    image: 'https://images.unsplash.com/photo-1695730866897-c7dbbdef09b0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    step: 6,
    flow: 'Multiplication'
  },
  {
    path: '/leaders',
    title: 'For Leaders',
    description: 'Small group guides and teaching tools',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1637109032180-ffda4ae86fbe?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    step: 7,
    flow: 'Multiplication'
  },
];

export default function HomePage() {
  const { isAuthenticated } = useAuth();
  const [dailyScripture, setDailyScripture] = useState(null);

  useEffect(() => {
    const fetchDailyScripture = async () => {
      try {
        const response = await axios.get(`${API}/scripture-cards/daily`);
        setDailyScripture(response.data);
      } catch (error) {
        console.error('Failed to fetch daily scripture:', error);
      }
    };
    fetchDailyScripture();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient relative py-16 sm:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
                From Gospel Understanding to Gospel Living
              </h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-xl">
                A discipleship platform guiding believers through a structured journey—from the 
                foundation of grace to the fruit of obedience. Rooted in Scripture. Centered on Christ.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {isAuthenticated ? (
                  <Link to="/dashboard">
                    <Button size="lg" className="btn-hover-lift w-full sm:w-auto" data-testid="hero-dashboard-btn">
                      Go to Dashboard
                      <ChevronRight className="h-5 w-5 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/register">
                      <Button size="lg" className="btn-hover-lift w-full sm:w-auto" data-testid="hero-get-started-btn">
                        Begin Your Journey
                        <ChevronRight className="h-5 w-5 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/gospel">
                      <Button variant="outline" size="lg" className="w-full sm:w-auto" data-testid="hero-explore-btn">
                        Explore the Gospel Hub
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
            
            {dailyScripture && (
              <div className="animate-slide-up delay-200">
                <ScriptureCard card={dailyScripture} className="shadow-xl" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Journey Flow */}
      <section className="py-16 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Your Discipleship Journey
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Progress from belief to identity, understanding to practice, obedience to multiplication.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {['Belief', 'Identity', 'Understanding', 'Practice', 'Obedience', 'Multiplication'].map((step, index) => (
              <div key={step} className="flex items-center animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  index === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {step}
                </span>
                {index < 5 && (
                  <ChevronRight className="h-4 w-4 text-muted-foreground mx-1 hidden sm:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hubs Grid */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Sparkles className="h-8 w-8 mx-auto text-primary mb-4" />
            <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Seven Hubs of Discipleship
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each hub contains teaching, quizzes, and practical tools to help you grow in grace and godliness.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {hubData.map((hub, index) => {
              const Icon = hub.icon;
              return (
                <Link 
                  key={hub.path} 
                  to={hub.path}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <Card 
                    className="hub-card h-full overflow-hidden group cursor-pointer"
                    data-testid={`hub-card-${hub.path.slice(1)}`}
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={hub.image}
                        alt={hub.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs">
                          Step {hub.step} · {hub.flow}
                        </span>
                      </div>
                    </div>
                    <CardHeader className="pb-2">
                      <CardTitle className="font-serif flex items-center gap-2">
                        <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                        {hub.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="line-clamp-2">
                        {hub.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-6">
            Ready to Grow in Grace?
          </h2>
          <p className="text-primary-foreground/80 mb-8 text-lg max-w-2xl mx-auto">
            Create a free account to track your progress, save journal entries, and measure your 
            understanding through Scripture-based quizzes.
          </p>
          {!isAuthenticated && (
            <Link to="/register">
              <Button 
                size="lg" 
                variant="secondary" 
                className="btn-hover-lift"
                data-testid="cta-register-btn"
              >
                Create Free Account
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-muted/50 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" strokeWidth={1.5} />
              <span className="font-serif font-semibold">Gospel Hub</span>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Sola Scriptura · Sola Gratia · Sola Fide · Solus Christus · Soli Deo Gloria
            </p>
            <p className="text-sm text-muted-foreground">
              Scripture quotations from ESV
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
